import type { IPackageSubscription } from "@/types"
import { differenceInDays } from "date-fns"
import { i18n } from "@/i18n"
import { PackageSubscriptionStatus } from "@/types"

export interface RenewalInfoResult {
  message: string
  isUrgent?: boolean
  isExpired?: boolean
}

/**
 * Calculates renewal information for a package subscription
 */
export function getRenewalInfo(packageSubscription?: IPackageSubscription): string | undefined {
  const { t } = i18n.global

  if (!packageSubscription?.packageSubscriptionId || !packageSubscription.expiryDate) {
    return undefined
  }

  const status = packageSubscription?.status ?? PackageSubscriptionStatus.Active
  const days = differenceInDays(new Date(packageSubscription?.expiryDate ?? new Date()), new Date())

  switch (status) {
    case PackageSubscriptionStatus.Active:
      if (packageSubscription.oneTimePayment) {
        if (days > 0)
          return t("package_subscription_card.renewal_info.expiring_in", { days })
        if (days === 0)
          return t("package_subscription_card.renewal_info.expired_today")
        return t("package_subscription_card.renewal_info.expired_ago", { days: Math.abs(days) })
      }
      if (days > 0)
        return t("package_subscription_card.renewal_info.renewing_in", { days })
      if (days === 0)
        return t("package_subscription_card.renewal_info.renewed_today")
      return t("package_subscription_card.renewal_info.renewed_ago", { days: Math.abs(days) })

    case PackageSubscriptionStatus.Cancelled:
      if (days > 0)
        return t("package_subscription_card.renewal_info.ending_in", { days })
      if (days === 0)
        return t("package_subscription_card.renewal_info.ended_today")
      return t("package_subscription_card.renewal_info.ended_ago", { days: Math.abs(days) })

    case PackageSubscriptionStatus.Expired:
      if (days < 0)
        return t("package_subscription_card.renewal_info.expired_ago", { days: Math.abs(days) })
      if (days === 0)
        return t("package_subscription_card.renewal_info.expired_today")
      return t("package_subscription_card.renewal_info.expiring_in", { days: Math.abs(days) })

    default:
      return undefined
  }
}
